/**
 * Audiences — live segments of the client book and the contact list.
 *
 * The counts on each card are computed on render, not stored, so they move as
 * the book moves. That is the whole point: a campaign sends to whoever
 * qualifies today, not whoever qualified when it was written.
 */
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { avatarClass } from '../../lib/format.js';
import { Card, CardHead, EmptyState, Pill } from '../../components/Primitives.jsx';
import { TileRow } from '../../components/StatCards.jsx';
import { AUDIENCE_FLAGS } from '../../constants/marketing.js';
import { audienceOf, reachable, audienceCoverage } from '../../domain/marketing.js';

export default function Audiences() {
  const { mkt, mktCtx, setSheet, addStarterSet } = useApp();

  /* Same count the Campaigns tiles show, from the same place — two screens
     disagreeing about how many people marketing covers is worse than either
     number being off. */
  const { covered, contactable } = audienceCoverage(mktCtx);

  const fromClients = mkt.audiences.filter(a => (a.src || 'clients') === 'clients').length;

  return (
    <>
      <TileRow
        tiles={[
          ['Audiences', mkt.audiences.length, 'target', 'g-purple'],
          ['From the ledger', fromClients, 'users', 'g-green'],
          ['From contacts', mkt.audiences.length - fromClients, 'users', 'g-blue'],
          ['People covered', covered, 'mega', 'g-pink'],
          ['Can be reached', contactable, 'users', 'g-blue'],
          ['Opted out', mkt.optouts.length, 'alert', 'g-red']
        ]}
      />

      <Card>
        <CardHead icon="target" tone="g-purple" title="Audiences" count={mkt.audiences.length}>
          <button type="button" className="btn sm primary" onClick={() => setSheet({ kind: 'audience' })}>
            <Icon name="plus" />New audience
          </button>
        </CardHead>

        {mkt.audiences.length ? (
          <div className="cards">
            {mkt.audiences.map(a => {
              const list = audienceOf(a, mktCtx);
              const emailable = reachable(list, 'email', mktCtx).length;
              const onWhatsApp = reachable(list, 'whatsapp', mktCtx).length;
              const flag = (AUDIENCE_FLAGS.find(f => f[0] === a.flag) || ['', ''])[1];

              return (
                <div className="ccard" key={a.id} onClick={() => setSheet({ kind: 'audience', id: a.id })}>
                  <div className="top2">
                    <div className={`who ${avatarClass(a.name)}`}><Icon name="target" /></div>
                    <div style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 14 }}>{a.name}</b>
                      <small style={{ color: 'var(--ink2)', fontSize: 12 }}>
                        {a.src === 'contacts' ? 'Leads and contacts' : 'Clients'}
                        {a.flag ? ` · ${flag}` : ''}
                      </small>
                    </div>
                  </div>

                  <div className="pills">
                    <Pill tone="p-purple">{list.length} people</Pill>
                    <Pill tone="p-blue">{emailable} emailable</Pill>
                    <Pill tone="p-green">{onWhatsApp} on WhatsApp</Pill>
                  </div>

                  <div className="foot">
                    <span>{a.plan || 'any plan'}</span>
                    <span>{a.owner || 'any owner'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState icon="target" tone="g-purple" title="No audiences yet">
            An audience is a live segment — build one and every campaign can use it.
            <div className="estart">
              <button type="button" className="btn primary" onClick={() => addStarterSet('audiences')}>
                Add the standard set
              </button>
              <span>Trials ending, payments overdue, renewals, free plans, churned accounts and more.</span>
            </div>
          </EmptyState>
        )}
      </Card>
    </>
  );
}
